/**
 * useSound — lightweight Web Audio sound system for game juice.
 *
 * All sounds are synthesized (no audio files needed). Default: muted.
 * Persists mute preference in localStorage.
 *
 * Sounds:
 *   expand  — soft pop (short sine burst)
 *   combine — resonant blend (two-tone chord)
 *   victory — celebratory ascending chord
 *   warmth  — subtle rising tone (getting closer to target)
 */

import { useRef, useState, useCallback } from 'react';

const STORAGE_KEY = 'lexiquest-sound';

function getInitialMuted() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === 'muted';
  } catch { return true; }
}

export default function useSound() {
  const ctxRef = useRef(null);
  const [muted, setMuted] = useState(getInitialMuted);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? 'muted' : 'unmuted'); } catch {}
      return next;
    });
  }, []);

  // --- Sound generators ---

  const playExpand = useCallback(() => {
    if (muted) return;
    const ctx = getCtx();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(900, now + 0.06);
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.15);
  }, [muted, getCtx]);

  const playCombine = useCallback(() => {
    if (muted) return;
    const ctx = getCtx();
    const now = ctx.currentTime;

    [440, 554, 659].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.03 + i * 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.02);
      osc.stop(now + 0.35);
    });
  }, [muted, getCtx]);

  const playVictory = useCallback(() => {
    if (muted) return;
    const ctx = getCtx();
    const now = ctx.currentTime;

    // Ascending major chord arpeggio
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.1);
      gain.gain.setValueAtTime(0, now + i * 0.1);
      gain.gain.linearRampToValueAtTime(0.1, now + i * 0.1 + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.6);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.6);
    });
  }, [muted, getCtx]);

  const playWarmth = useCallback(() => {
    if (muted) return;
    const ctx = getCtx();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(600, now + 0.2);
    gain.gain.setValueAtTime(0.06, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
  }, [muted, getCtx]);

  return {
    muted,
    toggleMute,
    playExpand,
    playCombine,
    playVictory,
    playWarmth,
  };
}
